import { File, type UploadResult } from 'expo-file-system';
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator';
import { Platform } from 'react-native';
import uuid from 'react-native-uuid';

import Constants from '@helpers/Constants';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import db from '@helpers/firebase/Firestore';
import functionsApi from '@helpers/firebase/Functions';
import { firestore, functions, storage } from '@helpers/firebase/Initialize';
import gcs from '@helpers/firebase/Storage';
import { Photo, Plan, PlanFormData } from '@types';
import { GetSignedPhotoUrlRequest, GetSignedPhotoUrlResponse } from '@types';

const MAX_DIM = 512;
const CACHE_MAX_AGE = 31536000;

const getSignedPhotoUrlFunction = functionsApi.httpsCallable<
  GetSignedPhotoUrlRequest,
  GetSignedPhotoUrlResponse
>(functions, 'photos-getSignedPhotoUrl');

export async function addPhotosToNewPlan(planId: string, photos: Photo[]) {
  const promises = photos.map((photo) => uploadPlanPhoto(planId, photo));
  const addedPhotos = await Promise.all(promises);
  await updatePlanDocWithPhotos(planId, addedPhotos);
  return addedPhotos;
}

export async function addPhotosToExistingPlan(planId: string, existingPlan: Plan, photos: Photo[]) {
  const currentPhotos = new Set(existingPlan.photos.map((photo) => photo.uri));
  const photosToAdd = photos.filter((photo) => !currentPhotos.has(photo.uri));
  const photosAlreadyAdded = photos.filter((photo) => currentPhotos.has(photo.uri));
  const photosNewlyAdded = await Promise.all(
    photosToAdd.map((photo) => uploadPlanPhoto(planId, photo))
  );
  // because we don't allow re-arranging the photos in the plan form,
  // the 'new' photos are guaranteed to come after the existing photos
  const planPhotos = photosAlreadyAdded.concat(photosNewlyAdded);
  await updatePlanDocWithPhotos(planId, planPhotos);
  return planPhotos;
}

export async function uploadPlanPhoto(planId: string, photo: Photo): Promise<Photo> {
  const userId = getCurrentUserId();
  const photoId = uuid.v4();
  const path = `users/${userId}/plans/${planId}/photos/${photoId}`;
  return await uploadImage(photo, path);
}

export async function updatePlanDocWithPhotos(planId: string, photos: Photo[]) {
  const update: Pick<PlanFormData, 'photos'> = { photos };
  return await db.updateDoc(db.doc(firestore, 'plans', planId), update);
}

export async function uploadProfilePhoto(photo: Photo): Promise<Photo> {
  const userId = getCurrentUserId();
  const photoId = uuid.v4();
  const path = `users/${userId}/profile/photos/${photoId}`;
  return await uploadImage(photo, path);
}

export async function uploadGroupPhoto(groupId: string, photo: Photo): Promise<Photo> {
  const photoId = uuid.v4();
  const path = `groups/${groupId}/photos/${photoId}`;
  return await uploadImage(photo, path);
}

export async function resizeImage(image: Photo) {
  const context = ImageManipulator.manipulate(image.uri);
  context.resize(image.height < image.width ? { height: MAX_DIM } : { width: MAX_DIM });
  const resizedImage = await context.renderAsync();
  const result = await resizedImage.saveAsync({ base64: true, format: SaveFormat.JPEG });

  context.release();
  resizedImage.release();

  return result;
}

type UploadImageOptions = {
  contentType: string;
  cacheControl: string;
};

const DEFAULT_IMAGE_OPTIONS: UploadImageOptions = {
  contentType: 'image/jpeg',
  cacheControl: `max-age:${CACHE_MAX_AGE}`,
};

async function uploadImage(
  image: Photo,
  path: string,
  options: UploadImageOptions = DEFAULT_IMAGE_OPTIONS
) {
  const resized = await resizeImage(image);
  let downloadUrl: string;
  // signed URLs are not supported on the Firebase emulator,
  // so we use the web upload capability when using it
  if (Platform.OS === 'web' || Constants.joinstar.JSEMULATE) {
    downloadUrl = await uploadImageWeb(resized.uri, path, options);
  } else {
    downloadUrl = await uploadImageNative(resized.uri, path, options);
  }
  if (downloadUrl) {
    return {
      uri: downloadUrl,
      height: resized.height,
      width: resized.width,
      html_attributions: image.html_attributions ? image.html_attributions : [],
    };
  } else {
    throw new Error('Error uploading image');
  }
}

// required to workaround firebase storage JS SDK issue
// https://github.com/firebase/firebase-js-sdk/issues/5848
async function uploadImageNative(
  uri: string,
  path: string,
  options: UploadImageOptions = DEFAULT_IMAGE_OPTIONS
) {
  const urlResult = await getSignedPhotoUrlFunction({ path });
  const signedUrl = urlResult.data.url;
  const file = new File(uri);
  const uploadResult = await file.upload(signedUrl, {
    httpMethod: 'PUT',
    headers: {
      'Content-Type': options.contentType,
      'Cache-Control': options.cacheControl,
    },
  });
  assertUploadSucceeded(uploadResult);
  const ref = gcs.ref(storage, path);
  const downloadUrl = await gcs.getDownloadURL(ref);
  return downloadUrl;
}

function assertUploadSucceeded(uploadResult: UploadResult) {
  if (uploadResult.status >= 200 && uploadResult.status < 300) return;

  throw new Error(
    `Error uploading image: signed URL upload failed with status ${uploadResult.status}`
  );
}

async function uploadImageWeb(
  uri: string,
  path: string,
  options: UploadImageOptions = DEFAULT_IMAGE_OPTIONS
) {
  const img = await fetch(uri);
  if (!img.ok) {
    throw new Error(`Error downloading image: HTTP ${img.status}`);
  }
  const blob = await img.blob();
  const ref = gcs.ref(storage, path);
  await gcs.uploadBytes(ref, blob, {
    cacheControl: options.cacheControl,
    contentType: options.contentType,
  });
  const downloadUrl = await gcs.getDownloadURL(ref);
  return downloadUrl;
}
